import type { CogniteClient } from '@cognite/sdk';
import { createContext, use } from 'react';

import type { DataModelView } from '#core/lib/cognite/view.ts';

import type { SourceFetcherMap } from '../EvalContext.ts';
import type { NameTable } from '../op-meta.ts';

/**
 * React-context-supplied extensions to the engine's `EvalContext`.
 * Threaded via context so consumer hooks (`useEvalExpr`,
 * `useMaterialize`, `useMaterializedExpr`) reach them without a
 * per-call options bag — the call site that builds the deps mounts
 * `<EvalDepsContext value={deps}>` once at the conversation root, and
 * every descendant evaluator hook picks them up.
 *
 * Every field is optional; `null` context resolves to engine defaults
 * (`defaultOpTable` / `defaultVerbTable`, no source fetchers, no
 * cognite callbacks). Production wiring lives in the chat plugin's
 * `<ChatSessionProvider>`; tests mount their own provider via the
 * shared `withEvalDeps` helper.
 */
export interface EvalDeps {
  /**
   * Custom op table for the evaluator (typically engine `builtins`
   * composed with domain ops like `knowledgeGraphOps`). Omit to pick
   * up `defaultOpTable` from `evalExpr`.
   */
  readonly ops?: NameTable;
  /**
   * Custom pipeline-verb table. Omit to pick up `defaultVerbTable`.
   */
  readonly verbs?: NameTable;
  /**
   * Per-source-kind fetcher dispatch handed to the materializer when a
   * `LazyDataset` is forced. The chat plugin registers `qkg` (and
   * future source kinds) here; non-CDF callers typically omit it.
   */
  readonly sourceFetchers?: SourceFetcherMap;
  /**
   * Live CDF-client accessor. Cognite-bound I/O ops
   * (`query-knowledge-graph`, future `from-view`, `fetch-datapoints`)
   * reach it through `requireCogniteClient` and surface
   * `MissingCallbackError` when absent.
   */
  readonly getCogniteClient?: () => CogniteClient;
  /**
   * Live view-registry accessor. `query-knowledge-graph` and any future
   * view-aware op call this through `requireViews` to resolve the
   * requested view at apply time.
   */
  readonly getViews?: () => readonly DataModelView[];
}

const EMPTY_EVAL_DEPS: EvalDeps = Object.freeze({});

/**
 * React context carrying the optional engine extensions described by
 * {@link EvalDeps}. `null` default means "use engine defaults"; the
 * consumer hook reads through {@link useEvalDeps} which converts
 * `null` to a stable empty object.
 *
 * Mount with React 19's context-as-provider syntax:
 *
 * ```tsx
 * <EvalDepsContext value={{ ops, sourceFetchers, getViews }}>
 *   {children}
 * </EvalDepsContext>
 * ```
 */
export const EvalDepsContext = createContext<EvalDeps | null>(null);
EvalDepsContext.displayName = 'ExprEvalDepsContext';

/**
 * Read the current {@link EvalDeps} from React context. Returns a
 * stable empty object when no provider is mounted, so consumers can
 * destructure without null-guarding.
 *
 * Unlike `useSession()`, this hook does not throw on a missing
 * provider — sensible defaults exist for every field, so missing
 * context just means "use them."
 */
export function useEvalDeps(): EvalDeps {
  return use(EvalDepsContext) ?? EMPTY_EVAL_DEPS;
}
